import { useFlags } from '@atlaskit/flag';
import { ICustomerNote } from 'core/api/customers/customers-api-interface';
import CustomersApiService from 'core/api/customers/customers-api.service';
import { AddCustomerNoteFormFields } from 'core/config/form-fields';
import { showErrorFlag } from 'core/utilities/flags-helper';
import { QuerySnapshot, Unsubscribe } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedForm from 'shared/components/form/form';
import { IAddCustomerNoteFormOutput, ICustomerOverviewNotes } from './customer-overview-notes-interface';
import { v4 as uuidv4 } from 'uuid';
import { useAuthState } from 'core/providers/AuthProvider';
import { ControlType } from 'core/enums/control-type';
import CustomerOverviewNote from './customer-overview-note';

const CustomerOverviewNotes = ({ customerId, customerName }: ICustomerOverviewNotes) => {
  const [showAddNoteInput, setShowAddNoteInput] = useState(false);
  const [notes, setNotes] = useState<ICustomerNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const flags = useFlags();
  const [formFields, setFormFields] = useState(AddCustomerNoteFormFields);
  const { userData } = useAuthState();
  const [editingUid, setEditingUid] = useState<string>();

  useEffect(() => {
    const handleSnapshot = (querySnapshot: QuerySnapshot<ICustomerNote>) => {
      const noteList: ICustomerNote[] = [];
      querySnapshot.forEach((noteSnapShot) => {
        noteList.push(noteSnapShot.data());
      });
      setNotes(noteList);
      setLoading(false);
    };
    const handleSubscriptionError = (error: any) => {
      showErrorFlag('An error occurred', 'Customer activity could not be retrieved, please try again.', flags);
    };
    let unsubscribe: Unsubscribe;
    unsubscribe = CustomersApiService.subscribeToCustomerNotes(customerId, handleSnapshot, handleSubscriptionError);
    return () => {
      unsubscribe();
    };
  }, [customerId, flags]);

  const cancelButton: IButton = {
    onClick: () => {
      setShowAddNoteInput(false);
      setFormFields(AddCustomerNoteFormFields);
    },
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const saveNote = async (data: IAddCustomerNoteFormOutput) => {
    setFormSubmitting(true);
    if (editingUid) {
      await CustomersApiService.updateCustomerNote({
        uid: editingUid,
        content: data.note,
        editDetail: {
          updatedAt: new Date(),
          editorName: userData?.fullName!,
          editorUid: userData?.uid!,
        },
        customerUid: customerId,
      });
    } else {
      await CustomersApiService.setCustomerNote({
        uid: uuidv4(),
        content: data.note,
        createdAt: new Date(),
        authorName: userData?.fullName!,
        authorUid: userData?.uid!,
        customerName: customerName,
        customerUid: customerId,
      });
    }
    setShowAddNoteInput(false);
    setFormSubmitting(false);
    setEditingUid(undefined);
    setFormFields(AddCustomerNoteFormFields);
    try {
    } catch (error) {
      showErrorFlag('Customer creation failed', `Unable to create this customer, please try again.`, flags);
      setFormSubmitting(false);
    }
  };

  const editNote = (note: ICustomerNote) => {
    setFormFields([
      {
        key: 'note',
        control: ControlType.TextArea,
        required: true,
        defaultValue: note.content,
      },
    ]);
    setShowAddNoteInput(true);
    setEditingUid(note.uid);
  };

  return (
    <div className='bg-white rounded-md shadow-md'>
      <div className='flex justify-between items-center p-4 border-b'>
        <p className='headline-06'>Customer notes</p>
        <SharedButton
          onClick={() => {
            setShowAddNoteInput(true);
          }}
          type='button'
          appearance='link'
          spacing='none'
          label='Add a note'
        />
      </div>
      {showAddNoteInput && (
        <div className='p-4 border-b'>
          <SharedForm
            onSubmit={saveNote}
            fields={formFields}
            buttonLabel='Save'
            loading={formSubmitting}
            cancelButton={cancelButton}
          />
        </div>
      )}
      {loading && (
        <div className='p-4'>
          <div className='h-[24px] w-[75%] bg-gray-100 rounded-md animate-pulse' />
          <div className='mt-1 h-[18px] w-[30%] bg-gray-100 rounded-md animate-pulse' />
        </div>
      )}
      {notes.map((note) => (
        <CustomerOverviewNote key={note.uid} note={note} editNote={editNote} />
      ))}
      {!loading && notes.length === 0 && <p className='body-02 text-gray-500 p-4'>No notes for this customer</p>}
    </div>
  );
};

export default CustomerOverviewNotes;
